// turning ai2html's output into a module allows
// webpack to apply hot module replacement.
// we obviously want to bake it into the html in production
// that's why we're conditionally requiring it

import { Child } from 'pym.js';
import meta from './data/meta.json';
import data from './data/data.json';
// Written by scripts/optimize-media.sh, not by the spreadsheet. See row().
import media from './data/media.json';
import Zion from './utils/Zion';


// styles
import './styles/index.scss';
import './styles/graphic.scss';

// Overlay records and cached offsets are mutated in place as assets load.
/* eslint-disable no-param-reassign */

const pkg = require('../package.json');

let ai2html = null;

if (process.env.NODE_ENV === 'development') {
  // turning ai2html's output into a module allows
  // webpack to apply hot module replacement.
  // we obviously want to bake it into the html in production
  // that's why we're conditionally requiring it
  ai2html = require('./ai2html-graphic.html');
}


/* ------------------------------------------------------------------ *
 * Config
 * ------------------------------------------------------------------ */

// Illustrator layer holding the scroll-revealed text annotations. ai2html
// writes the layer name onto each text div as `g-<layer name>`. Note this can't
// be the `trigger` layer — that one is `:svg,inline`, so anything on it is
// exported as vector paths rather than text divs.
const ANNOTATION_SELECTOR = '.g-annotation';

// Illustrator layer holding the map labels that come and go with the map rather
// than being baked into it. Most are named for the point they belong to and are
// switched by that point's card; one named for no point on the board — see
// spareLabels() — is a step of its own with nothing to show but its copy.
const LABEL_SELECTOR = '.g-trigger-text';

// Illustrator layer of arrows drawn on the river, and the label they belong with.
//
// Unlike a label, the arrows are named for no point and stand for no stop on the
// river — they're part of the closing note about how far downstream the flood
// carried, so they come up on that note's position rather than on their own. See
// spareLabels().
const ARROW_SELECTOR = 'svg [id="arrow"]';
const ARROW_LABEL = 'point11-text';

// Fraction of the way up the parent viewport at which an element reveals.
// 0 = bottom edge of the screen, 1 = top edge.
const REVEAL_AT = 0.35;

// Least time a card holds the screen once it's up, in ms. A card's
// natural turn lasts only as long as it takes the reader to reach the next point
// down the river, and the points aren't evenly spaced: point 1 sits 138px above
// point 2, a sixth of a second apart at a normal scroll speed, so its card was
// always swapped out mid-fade and never actually seen.
//
// Has to stay above the 0.5s fade in graphic.scss. Below that it's worse than no
// hold at all, since it delays the handover without leaving the card it's
// holding time to fade in. See tick().
//
// This is also the only brake on the pace that doesn't cost page. Spacing the
// triggers further apart is the other way to slow the map down, and on mobile
// there is nothing left to spend: the points span 1536px, the floors already ask
// 1820px of it, and a trigger can't be pushed more than about half a screen past
// its own point without the point leaving the screen before its clip arrives —
// so widening the floor only makes the run of close points at the bottom clamp
// against that limit and hand over faster than they do now. Time is free of all
// of that.
const MIN_SHOW_MS = 900;

// Progress past which a point is too near the top of the parent viewport to be
// worth handing its card over to: it would finish fading in as the point left
// the screen. 1 is the top edge. Used to decide which points a handover may step
// through — see nextCard().
const SHOW_UNTIL = 0.9;

// How far below its own ceiling a point reveals when it can't reach REVEAL_AT,
// as a fraction of the viewport. Only the last point or two on a map are ever
// short enough of scroll for this to apply. Large enough that the clip is up
// before the reader hits the end of the page and has a moment to be watched;
// much larger and it would take the turn off the point above it. See tick().
const REVEAL_MARGIN = 0.1;

// The opening card gets longer than the rest. It's the reader's introduction to
// the mechanic, and there's no scroll momentum to keep up with yet, so a plain
// MIN_SHOW_MS would leave it fully opaque for only the 100ms after its fade.
// Beyond about 1.2s it starts costing the second card its turn.
const OPENING_SHOW_MS = 1200;

// How long each step of the opening holds before the next takes over, in ms.
//
// The top of the map is where the flood started and where the scroll mechanism
// has least to work with: the glacier, the two lakes below it and the border
// crossing sit inside 200px of artboard, which is nowhere near the scroll it
// takes to give three cards a turn, and the reader has barely started scrolling
// when they come up. So the opening plays itself instead, and asks for no
// scrolling at all.
//
// One entry per handover, so the opening runs one step longer than this is long:
// the glacier for 2s, the lakes for 5s, then the border crossing, which holds
// until the reader scrolls past it. See introCard().
const INTRO_HOLDS = [2000, 5000];

// Least scroll distance an ordinary card gets before the next point can
// take the screen, in pixels. Honoured even where the map is too short to hold
// every point's share of it, which is what END_RUNWAY pays for. See
// triggerGaps().
const MIN_TRIGGER_GAP = 200;

// What a card gets instead when the next point is stacked on top of it. Points 6
// and 7 sit 4px apart on the artboard, so point 6's clip had a 4px turn and was
// gone before it could be watched. Held well above MIN_TRIGGER_GAP because a
// stacked pair is the one place the reader can cross two points in a single
// flick of the wheel.
const CLUSTER_TRIGGER_GAP = 420;

// How close two points have to be, in pixels down the artboard, to count as one
// spot on the map rather than two stops on the river.
//
// Set against the mobile artboard, which is the crowded one: the pairs it has to
// catch are points 1 and 9 at 11px apart and points 8 and 7 at 78px, and the
// tightest ordinary pair it has to leave alone is points 6 and 5 at 138px. The
// two bridges at the bottom of the map are the reason this isn't lower — 78px is
// a couple of hundred feet of river and reads as one place, but it used to fall
// outside this window and the two clips were handed over between in the 200px
// floor, which is the fastest the end of the map ever went.
//
// Desktop and widedesktop have no pair between 15px and 107px, so nothing there
// moves either side of this.
const CLUSTER_WITHIN = 90;

// Fraction of the theoretical ceiling on how far a trigger may sit past its own
// point. That ceiling isn't a matter of taste: a trigger delayed by more than
// SHOW_UNTIL - REVEAL_AT viewports fires only once the point has climbed off the
// top of the screen, and nextCard() then refuses the card outright. Held under
// it because a trigger is crossed on the way past REVEAL_AT rather than exactly
// on it. See maxTriggerDelay().
const TRIGGER_DELAY_MARGIN = 0.9;

// Scroll held below the last trigger, as a fraction of the reader's screen.
//
// Every card but the last takes its turn and hands over to the next point. The
// last one has only whatever page is left underneath it, and the artboards end
// close behind the final point: 257px on mobile375, where a card needs
// REVEAL_AT of a screen — 245px at a typical height — just to climb to its
// reveal line. Left short, the last two cards come up at the same instant, since
// the fallback bar in tick() fires every point it governs together, and with no
// scroll left for the handover the reader only ever sees the first of them.
//
// REVEAL_AT of a screen is what carries the last trigger to its line, and that
// is all this buys. It used to buy a fifth of a screen beyond that for the card
// to take its turn in, which is page the reader has to scroll past an empty map
// to reach the end of — 140px of it on mobile375, 160px on desktop.
//
// The turn is paid for in time instead, by two things that didn't exist when
// this was a fifth of a screen taller. tick() waives the scroll half of the
// handover gate once the reader is grounded at the end of the graphic, so the
// last cards settle on MIN_SHOW_MS alone rather than on pixels that aren't
// coming. And the bar a trigger is measured against drops to what it can
// actually reach, less REVEAL_MARGIN, so the last card still comes up a tenth of
// a screen short of the bottom rather than on the final pixel.
//
// See padForOverlays().
const END_RUNWAY = REVEAL_AT;

// Stands in for the parent viewport until it has answered once, only to keep the
// first spacing pass sane. A parent that never answers reveals everything.
const FALLBACK_VIEWPORT = 700;

// How far the reader has to have moved since the current card came up before it
// will hand over, in pixels of parent scroll.
//
// The handover in tick() steps one point at a time, so it is often behind the
// reader and wanting to catch up: on load, where the reader starts out past the
// first point or two, and through the stacked points, where a single read
// crosses three of them. Without this it closes that gap on the clock alone,
// swapping cards out while the page sits perfectly still. Small enough that on a
// real scroll it's MIN_SHOW_MS that governs the pace, not this.
//
// Waived once the reader has run out of graphic to scroll, where holding out for
// pixels that aren't coming strands the bottom of the map. See tick().
const SCROLL_STEP = 40;

// How many viewports ahead of the reader to start downloading an asset.
const PRELOAD_VIEWPORTS = 1.5;

// Illustrator layer holding the line the flood took. ai2html bakes the layer
// name into the id of the group it exports, so matching on the name here means
// the trace survives a re-export without anything needing renaming.
const TRACE_LAYER = 'landslide';

// Places along the path to try when working out where a dot sits on it. The
// dots are a separate layer and know nothing about the line, so the two are
// matched by walking the path and keeping the nearest place. Coarse pass to find
// the neighbourhood, then a short fine pass inside it.
const TRACE_SAMPLES = 1200;
const TRACE_REFINE = 40;

// Milliseconds of drawing per unit of path, and the range it clamps into. Paced
// by length rather than giving every leg the same duration, so the line travels
// at one speed: the run down to point 8 is a quarter of the whole path, four
// times the leg above it. Set so a typical leg lands near the 600ms a card holds
// for, which puts the line arriving as the clip it belongs to finishes fading.
const TRACE_MS_PER_UNIT = 1.4;
const TRACE_MIN_MS = 350;
const TRACE_MAX_MS = 1600;

// pym's Parent answers `parentPositionInfo` on demand but only pushes scroll
// events when built with `trackscroll: true`, which the CMS does not do.
// Polling from the child is the only thing that works in every parent.
const POLL_MS = 100;

// Clear space kept between a point and its own popup, in artboard pixels, and
// so also the length of the leader line drawn across it. Also the minimum the
// popup gets pushed away if offsets would cover the point.
const POINT_GAP = 28;

// What a popup card adds either side of its asset: the 5px padding and 1px
// border from .overlay-container in graphic.scss. Only used to work out whether
// a card will fit beside a point, so it doesn't have to be exact.
const POPUP_CHROME = 12;

// Clear space held under the lowest popup, in artboard pixels. The bottom point
// sits near the end of the artboard, so its card hangs past it and would
// otherwise finish flush with the end of the graphic. See padForOverlays().
const OVERLAY_BOTTOM_GAP = 20;

// Used for any column the spreadsheet doesn't have yet.
//
// What caps the widedesktop figure is point 2, the leftmost point at x=478: a
// card has to fit between it and the edge of the artboard to stay on the left of
// its point. That runs out at about 450, and 340 leaves 107px of margin.
const DEFAULT_SIZES = '160,180,260,340';
const DEFAULT_OFFSETS = '0,0,0,0';

// How tall a popup's clip may get relative to the popup's width, before
// the height is what limits it. This is the knob for how much presence a
// vertical clip has against a landscape one: at 1, portrait clips are square-ish
// and small; the taller it goes the wider they get, until at the 9:16 clips'
// own ratio of about 1.76 they'd be as wide as a landscape popup.
const PORTRAIT_MAX_HEIGHT = 1.35;

// Floor for a portrait popup's width, since a very tall clip can otherwise be
// left too narrow to set a caption in.
const MIN_POPUP_WIDTH = 120;

const VIDEO_FILE = /\.(mp4|webm|mov|m4v)$/i;

const FALSEY = /^(no|false|0|n)$/i;

// Speaker glyphs for the sound toggle. Muted state shows the crossed-out one.
const ICON_SOUND_OFF = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M11 5 6.5 9H3v6h3.5L11 19V5z"/><path d="M15.5 9.5l5 5m0-5-5 5" fill="none" stroke-width="1.8"/></svg>';
const ICON_SOUND_ON = '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M11 5 6.5 9H3v6h3.5L11 19V5z"/><path d="M15 8.5a4.5 4.5 0 0 1 0 7M17.5 6a8 8 0 0 1 0 12" fill="none" stroke-width="1.8"/></svg>';

// How long to wait for a parent to answer before giving up and showing
// everything.
const FALLBACK_MS = 1500;


// Mobile browser chrome collapsing changes the reported viewport height by
// 60-100px mid-scroll. Re-spacing the triggers on that would move the cards'
// turns under the reader, so only a change too big to be chrome counts as a
// real resize.
const VIEWPORT_SLOP = 140;


/* ------------------------------------------------------------------ *
 * State
 * ------------------------------------------------------------------ */

// Tracked nodes, with their offset from the top of the iframe cached so the
// scroll tick never has to touch the layout.
let triggers = [];
let overlays = [];
// Map labels no card speaks for, which reveal against their own position on the
// way past instead. See spareLabels().
let labels = [];

let lastWidth = window.innerWidth;
let buildTimer = null;
let fallbackTimer = null;
let listening = false;
let gotPosition = false;

// Whether a report about a frame that isn't this one has already been called
// out. Said once: they arrive on every poll. See tick().
let warnedStray = false;

// Height of the reader's screen, as last reported by the parent. Nothing in the
// iframe can measure it — `window.innerHeight` in here is the frame's own height
// — and how far a trigger may be spaced from its point depends on it.
let parentViewport = 0;

// Which card is showing, and when it took over. Kept so a card that comes up
// just before the reader reaches the next point still gets MIN_SHOW_MS on
// screen.
let shown = -1;
let shownAt = 0;
// Where the parent was when it came up, so a handover can tell whether the
// reader has actually scrolled since.
let shownTop = 0;
// Whether the opening card has had its turn, which is longer than the rest.
let opened = false;

// When the timed opening started running, and whether it has handed the sequence
// over to the scroll mechanism for good. See introCard().
let introFrom = 0;
let introOver = false;

// Overlay indexes in river order, top of the map down. The sheet's rows don't
// follow the map, so this is what "the next card" means.
let order = [];

// The flood line, once found on the artboard: the path itself, its length, which
// end of it is the top of the map, and how much is drawn. Null on an artboard
// whose export doesn't carry the layer.
let trace = null;


/* ------------------------------------------------------------------ *
 * Geometry helpers
 * ------------------------------------------------------------------ */

function toSvg(svg, x, y) {
  const point = svg.createSVGPoint();
  point.x = x;
  point.y = y;
  return point.matrixTransform(svg.getScreenCTM().inverse());
}


function clamp(value, min, max) {
  return Math.min(Math.max(value, min), max);
}


// Falls back to the last supplied value so a short list degrades instead of
// handing NaN to the layout.
function valueAt(list, index) {
  const values = String(list).split(',').map(Number);
  return values[Math.min(index, values.length - 1)] || 0;
}


// Same indexing as valueAt, for comma-separated sheet strings. A single value
// applies to every artboard; a short list falls back to its last entry.
function valueAtStr(list, index) {
  const values = String(list || '').split(',').map(s => s.trim().toLowerCase());
  if (!values.length || !values.some(Boolean)) return '';
  return values[Math.min(index, values.length - 1)] || '';
}


// The nth comma-separated value in sizes / hOffsets / vOffsets belongs to the
// nth-narrowest artboard. Deriving the order from the artboards actually in the
// document beats hardcoding breakpoints, because artboard widths vary by
// template — this one's mobile375 board is 343px wide, not 375.
function breakpointIndex(artboard) {
  const mins = Array.from(document.querySelectorAll('.g-artboard'))
    .map(ab => parseFloat(ab.dataset.minWidth) || 0)
    .sort((a, b) => a - b);

  const min = parseFloat(artboard.dataset.minWidth) || 0;
  return Math.max(0, mins.indexOf(min));
}


// Offset from the top of the iframe document. Stable until the next re-layout,
// because a pym iframe is expanded to its full height and never scrolls.
function documentTop(node) {
  return node.getBoundingClientRect().top + window.pageYOffset;
}


// Exactly one artboard is displayed at a time; the rest are `display: none`.
function visibleArtboard() {
  const artboards = Array.from(document.querySelectorAll('.g-artboard'));
  return artboards.find(ab => window.getComputedStyle(ab).display !== 'none');
}


// One spreadsheet row from the `data` tab, filled out with defaults for the
// columns that don't exist yet. `point-name` is the object on the Illustrator
// `trigger` layer that the clip hangs off and reveals against; `asset-name` is
// the file in public/media.
function row(d) {
  const name = String(d['asset-name'] || '').trim();
  const point = String(d['point-name'] || d.point || '').trim();
  const isVideo = VIDEO_FILE.test(name);
  const base = name.replace(/\.[^.]+$/, '');

  // optimize-media.sh writes every clip as .mp4 and every still as .jpg
  // whatever the source format was, so the extension in the sheet is read as
  // the designer's export rather than as the name of the file being served.
  // Without this a still exported as a .png needs its cell edited by hand after
  // every optimising pass, and a cell that gets missed is a broken image.
  const file = base && `${base}${isVideo ? '.mp4' : '.jpg'}`;

  // Extra dots that light up with this card but don't get one of their own. A
  // step can be about more than one spot on the river — the barrier lakes are
  // two points and a single clip. Only `point-name` positions the card and
  // takes the leader line; these just come along for the highlight.
  const alsoLight = ['point-name2', 'point-name3']
    .map(column => String(d[column] || '').trim())
    .filter(Boolean);

  const audio = String(d.audio || '').trim();

  return {
    pointId: point || base,
    alsoLight,
    src: d.src || (file && `./media/${file}`),
    isVideo,
    // Clips are silent by default and the reader opts in, so one with no audio
    // track shouldn't advertise a button that does nothing. Answered from the
    // manifest optimize-media.sh writes, since the encoder already probes every
    // clip to decide whether to keep its audio. The sheet's `audio` column still
    // wins where it's filled in, for a clip whose track is there but not worth
    // offering. Blank falls through to the manifest.
    hasAudio: audio ? !FALSEY.test(audio) : media[file] === undefined || media[file].audio,
    // `description` is the column `caption` replaced, read as a fallback so a
    // sheet still using the old header keeps setting copy.
    caption: d.caption || d.description,
    credit: d.credit,
    // optimize-media.sh writes one of these alongside every clip. It's applied
    // when the clip is fetched rather than up front, because a poster downloads
    // the moment it's assigned: setting seven at build time would pull every
    // one of them on open and undo the lazy loading.
    poster: d.poster || (isVideo && base ? `./media/${base}-poster.jpg` : ''),
    orientation: d.orientation,
    lineStyle: d['line-style'] || d.lineStyle || '',
    sizes: d.sizes || DEFAULT_SIZES,
    hOffsets: d.hOffsets || DEFAULT_OFFSETS,
    vOffsets: d.vOffsets || DEFAULT_OFFSETS,
  };
}


// Captions are set as HTML so the sheet can carry a link or an italic, which
// makes them unusable as alt text until the markup is taken back out.
function stripTags(html) {
  const el = document.createElement('div');
  el.innerHTML = html || '';

  return (el.textContent || '').trim();
}


// With no `orientation` column, popups go to the left of their point. The map
// sits right of centre on the widedesktop artboard, so that's the side with the
// room, and holding one side reads better than alternating across the river.
//
// The right is only used for a point with no room for a popup beside it, which
// is what keeps a card on the artboard if a point moves left.
function autoOrientation(point, artboard, size) {
  const p = point.getBoundingClientRect();
  const a = artboard.getBoundingClientRect();

  // Widest the card can end up: mediaWidth() never returns more than `size`,
  // and the card's border and padding go on top of that.
  const widest = size + POPUP_CHROME + POINT_GAP;

  return p.left - a.left >= widest ? 'left' : 'right';
}


/* ------------------------------------------------------------------ *
 * Playback and sound
 * ------------------------------------------------------------------ */

// Autoplay is rejected outright in parents that don't pass through the
// `allow` attribute; the poster frame stands in for the clip there.
function play(video) {
  const playing = video.play();
  if (playing !== undefined) playing.catch(() => {});
}


function setSoundButton(overlay, on) {
  const { button } = overlay;
  if (!button) return;

  button.innerHTML = on ? ICON_SOUND_ON : ICON_SOUND_OFF;
  button.setAttribute('aria-pressed', on ? 'true' : 'false');
  button.setAttribute('aria-label', on ? 'Turn off sound' : 'Turn on sound');
  button.classList.toggle('is-on', on);
}


function mute(overlay) {
  if (!overlay.video || overlay.video.muted) return;

  overlay.video.muted = true;
  setSoundButton(overlay, false);
}


// Clips stay muted until the reader asks for sound, and only one is ever
// audible — otherwise scrolling past an unmuted clip layers it under the next.
function toggleSound(overlay) {
  const turningOn = overlay.video.muted;

  if (turningOn) {
    overlays.forEach((o) => { if (o !== overlay) mute(o); });
    overlay.video.muted = false;
    // Unmuting counts as the user gesture iOS wants, so it's a good moment to
    // start a clip whose muted autoplay was refused.
    play(overlay.video);
  } else {
    overlay.video.muted = true;
  }

  setSoundButton(overlay, turningOn);
}


/* ------------------------------------------------------------------ *
 * Overlays
 * ------------------------------------------------------------------ */

// A ring drawn behind the point, in the point's own SVG coordinates, that
// pulses while the clip is showing. Pairing a clip to its spot on the river is
// otherwise guesswork once the popup is offset away from it.
function addPulse(anchor) {
  const box = anchor.getBBox();
  const pulse = document.createElementNS('http://www.w3.org/2000/svg', 'circle');

  pulse.setAttribute('class', 'point-pulse');

  // Which dot this ring belongs to. The ring carries no id of its own, and sits
  // in the same <g> as every other, so without this the stylesheet has no way to
  // pick out the one point that isn't coloured like the rest.
  pulse.setAttribute('data-point', anchor.id);

  pulse.setAttribute('cx', box.x + (box.width / 2));
  pulse.setAttribute('cy', box.y + (box.height / 2));
  pulse.setAttribute('r', Math.max(box.width, box.height) / 2);

  // Behind the point itself, so the dot stays crisp on top of the ring.
  anchor.parentNode.insertBefore(pulse, anchor);

  return pulse;
}


// Lights or dims every dot a card speaks for, which for most cards is the one
// it hangs off.
function setHighlight(overlay, on) {
  overlay.highlights.forEach(({ node, pulse }) => {
    node.classList.toggle('is-active', on);
    if (pulse) pulse.classList.toggle('is-active', on);
  });
}


// Start and end of the leader line in screen coordinates. The cross-axis
// coordinate is clamped so the line still lands on the card's facing edge when
// an offset has slid the popup off-axis.
function lineEndpoints(overlay, boxRect, anchorRect) {
  const { orientation } = overlay;

  const anchorCenterX = anchorRect.left + (anchorRect.width / 2);
  const anchorCenterY = anchorRect.top + (anchorRect.height / 2);

  if (orientation === 'left' || orientation === 'right') {
    const y = clamp(anchorCenterY, boxRect.top, boxRect.bottom);
    const leftward = orientation === 'left';

    return {
      from: { x: leftward ? anchorRect.left : anchorRect.right, y: anchorCenterY },
      to: { x: leftward ? boxRect.right : boxRect.left, y },
    };
  }

  const x = clamp(anchorCenterX, boxRect.left, boxRect.right);
  const downward = orientation === 'bottom';

  return {
    from: { x: anchorCenterX, y: downward ? anchorRect.bottom : anchorRect.top },
    to: { x, y: downward ? boxRect.top : boxRect.bottom },
  };
}


// The leader line lives in the anchor's own <g> so it inherits the SVG's
// coordinate system and scales with the artboard.
function drawLine(overlay) {
  const {
    box, anchor, svg, orientation, lineStyle,
  } = overlay;

  if (overlay.line) overlay.line.remove();

  // The card's laid-out box, not its painted one.
  //
  // Lines are drawn during layout, which runs while a card is still hidden, and
  // a hidden card carries the fade's `translateY(10px)`. getBoundingClientRect
  // reports that shift, so a line measured against it is drawn to where the card
  // sits before it rises rather than where it comes to rest. Revealing then
  // moves the card 10px out from under the join — under the card and invisible
  // for a placement joined along its top edge, but a card placed above its point
  // is joined along its bottom, and there the line is left hanging short of it.
  //
  // offsetTop and its siblings ignore transforms, and are measured from the
  // artboard, which is the card's offset parent.
  const artboardRect = box.parentNode.getBoundingClientRect();

  const boxRect = {
    left: artboardRect.left + box.offsetLeft,
    top: artboardRect.top + box.offsetTop,
    right: artboardRect.left + box.offsetLeft + box.offsetWidth,
    bottom: artboardRect.top + box.offsetTop + box.offsetHeight,
  };

  const anchorRect = anchor.getBoundingClientRect();
  const { from, to } = lineEndpoints(overlay, boxRect, anchorRect);

  const start = toSvg(svg, from.x, from.y);
  const end = toSvg(svg, to.x, to.y);

  let line;

  if (lineStyle === 'elbow' && from.x !== to.x && from.y !== to.y) {
    const corner = (orientation === 'left' || orientation === 'right')
      ? { x: to.x, y: from.y }
      : { x: from.x, y: to.y };
    const bend = toSvg(svg, corner.x, corner.y);

    line = document.createElementNS('http://www.w3.org/2000/svg', 'polyline');
    line.setAttribute(
      'points',
      `${start.x},${start.y} ${bend.x},${bend.y} ${end.x},${end.y}`,
    );
  } else {
    line = document.createElementNS('http://www.w3.org/2000/svg', 'line');
    line.setAttribute('x1', start.x);
    line.setAttribute('y1', start.y);
    line.setAttribute('x2', end.x);
    line.setAttribute('y2', end.y);
  }

  line.setAttribute('class', 'overlay-line fade');
  anchor.parentNode.appendChild(line);
  overlay.line = line;
}


// A `sizes` value is a popup's width, which a portrait clip keeps until its
// height would run past PORTRAIT_MAX_HEIGHT times that — after which height is
// what's capped and the width follows from the aspect ratio.
//
// Capping the longest edge instead, which is what this did first, holds the area
// roughly even between orientations but reads as far too small: a 9:16 clip came
// out a 159px column beside a 280px landscape one. Sizing purely by width is the
// other extreme, since that same clip would then be three times as tall as the
// landscape one and swamp the map.
//
// Returns the size unchanged until the asset's own dimensions are known.
function mediaWidth(overlay) {
  const { img, video, size } = overlay;

  const w = video ? video.videoWidth : img && img.naturalWidth;
  const h = video ? video.videoHeight : img && img.naturalHeight;

  if (!w || !h || h <= w) return size;

  const byHeight = size * PORTRAIT_MAX_HEIGHT * (w / h);

  return Math.max(MIN_POPUP_WIDTH, Math.round(Math.min(size, byHeight)));
}


// Places the popup in artboard coordinates and redraws its leader line.
// Safe to call again whenever the popup's size changes.
function layout(overlay) {
  const {
    box, anchor, svg, orientation, hOffset, vOffset,
  } = overlay;

  // A marker row has a point but no card to place against it, and a card left
  // over from a previous build has no artboard to place it on: loading is
  // asynchronous, so a clip fetched before a breakpoint change can arrive after
  // the box it belongs to has been taken out of the document.
  if (!box || !box.parentNode) return;

  const artboard = box.parentNode;

  // The asset governs the card's width, and its dimensions only arrive once it
  // has loaded, so this is re-applied on every pass rather than set on create.
  box.style.width = `${mediaWidth(overlay)}px`;

  // getBoundingClientRect forces a synchronous layout, so these read the
  // popup's current content rather than a stale box.
  const anchorRect = anchor.getBoundingClientRect();
  const artboardRect = artboard.getBoundingClientRect();
  const boxRect = box.getBoundingClientRect();
  const svgRect = svg.getBoundingClientRect();

  const anchorCenterX = anchorRect.left + (anchorRect.width / 2);
  const anchorCenterY = anchorRect.top + (anchorRect.height / 2);

  // Anchor position relative to the artboard.
  const centerX = anchorCenterX - artboardRect.left;
  const centerY = anchorCenterY - artboardRect.top;
  const anchorTop = anchorRect.top - artboardRect.top;
  const anchorBottom = anchorRect.bottom - artboardRect.top;
  const anchorLeft = anchorRect.left - artboardRect.left;
  const anchorRight = anchorRect.right - artboardRect.left;

  const scale = svgRect.width / svg.viewBox.baseVal.width;
  const gap = POINT_GAP * scale;

  // Placed above its point, which is also what an unrecognised orientation
  // falls back to — see the last branch below.
  const upward = !['bottom', 'left', 'right'].includes(orientation);

  let left;
  let top;

  if (orientation === 'bottom') {
    left = centerX - (boxRect.width / 2);
    top = anchorBottom + gap;
  } else if (orientation === 'left') {
    left = anchorLeft - boxRect.width - gap;
    top = centerY - (boxRect.height / 2);
  } else if (orientation === 'right') {
    left = anchorRight + gap;
    top = centerY - (boxRect.height / 2);
  } else {
    left = centerX - (boxRect.width / 2);
    top = anchorTop - boxRect.height - gap;
  }

  left += hOffset;
  top += vOffset;

  // Offsets are tuned by hand, so they can easily drag a popup back over the
  // point it belongs to. Only test overlap on the placement axis — a left/right
  // card is centred on the point vertically and always intersects it on that
  // axis, so a full bounding-box test would reject any horizontal nudge inward.
  // Clamp to the point's edge rather than snapping back to the default gap,
  // which would discard the offset entirely.
  if (orientation === 'left') {
    if (left + boxRect.width > anchorLeft) {
      left = anchorLeft - boxRect.width;
    }
  } else if (orientation === 'right') {
    if (left < anchorRight) {
      left = anchorRight;
    }
  } else if (orientation === 'bottom') {
    if (top < anchorBottom) {
      top = anchorBottom;
    }
  } else if (top + boxRect.height > anchorTop) {
    top = anchorTop - boxRect.height;
  }

  // A side-placed card is centred on its point, so the last point on the map
  // leaves its card hanging off the bottom of the imagery. Nudge it back up.
  //
  // Only side placements: those are already clear of the point horizontally, so
  // moving them vertically can't put them back over it. Moving a top- or
  // bottom-placed card vertically could, and would undo the placement anyway.
  //
  // Measured against the SVG, which is the map itself, rather than against the
  // artboard box the map happens to be laid into.
  if (orientation === 'left' || orientation === 'right') {
    const bottomGap = OVERLAY_BOTTOM_GAP * scale;
    const floor = svgRect.height - bottomGap - boxRect.height;

    // A card too tall to fit above the floor is left overhanging for
    // padForOverlays() to make room for, rather than pushed off the top.
    if (top > floor) top = Math.max(bottomGap, floor);
  }

  box.style.left = `${left}px`;

  // Held by the edge that faces the point, which for every placement but one is
  // the edge `top` already names.
  //
  // A card above its point is the exception: it is placed by its top and joined
  // by its bottom, so holding it the usual way leaves the joined edge wherever
  // the card's height happened to put it at the moment it was measured. Anything
  // that reflows the card after that — a webfont arriving and rewrapping the
  // caption, a clip resolving to a shape the first pass couldn't know — slides
  // that edge out from under the leader line and opens a gap. Held from the
  // bottom, the card grows upward into empty map and the join stays put.
  if (upward) {
    box.style.top = 'auto';
    box.style.bottom = `${artboardRect.height - (top + boxRect.height)}px`;
  } else {
    box.style.bottom = 'auto';
    box.style.top = `${top}px`;
  }

  drawLine(overlay);
}


// An empty block after the artboards, carrying whatever page the graphic needs
// below the map. Kept as a sibling rather than padding on the artboard itself:
// ai2html places every label and annotation absolutely at a percentage `top`,
// which resolves against the artboard's padding box, so padding the artboard
// slides all of them down the map while the image — pinned at `top: 0`, its
// height coming from its own aspect ratio — stays where it is.
function endSpacer(artboard) {
  const box = artboard.parentNode;
  const found = box.querySelector('.end-runway');
  if (found) return found;

  const spacer = document.createElement('div');
  spacer.className = 'end-runway';
  spacer.setAttribute('aria-hidden', 'true');
  box.appendChild(spacer);

  return spacer;
}


// Page held below the map, for two reasons that both come out as height on the
// spacer.
//
// Absolutely positioned popups don't contribute to the document height, so a
// popup hanging off the bottom of the last artboard gets cut off by the iframe.
// The space carries OVERLAY_BOTTOM_GAP beyond the lowest card, so the bottom
// clip ends clear of the graphic rather than flush with it.
//
// And the last point needs scroll under it to take its turn in — END_RUNWAY of
// a screen past the trigger, not past the point, since spaceTriggers() may have
// pushed the two apart. This is self-cancelling: extend the artboard below the
// last point in Illustrator and the spacer gives way to map by the same amount.
function padForOverlays(artboard) {
  const spacer = endSpacer(artboard);

  // Reset first, so the edges read below are the graphic's own.
  spacer.style.height = '0px';
  if (!overlays.length) return;

  const { bottom } = artboard.getBoundingClientRect();

  // Seeded low, not at `bottom`: a graphic whose cards all sit well inside the
  // artboard shouldn't pick up trailing whitespace it has no use for.
  let lowest = -Infinity;

  overlays.forEach((o) => {
    if (o.box) lowest = Math.max(lowest, o.box.getBoundingClientRect().bottom);
  });

  // Undefined until measure() has run a spacing pass, and NaN here would eat the
  // card allowance alongside it.
  const last = overlays[order[order.length - 1]];
  const end = last && Number.isFinite(last.trigger)
    ? (last.trigger + (END_RUNWAY * (parentViewport || FALLBACK_VIEWPORT)))
      - document.documentElement.scrollHeight
    : 0;

  const pad = Math.max((lowest + OVERLAY_BOTTOM_GAP) - bottom, end);

  if (pad > 0) {
    spacer.style.height = `${Math.ceil(pad)}px`;
  }
}


// An asset only reports its size once it has loaded, and until then the card is
// the height of its caption alone. So the card has to be re-placed, and the
// artboard re-padded, off the back of every load — the first pass in build()
// can't know how far the bottom clip reaches.
function relayout(overlay) {
  // Same reason layout() checks: the load this is hanging off may land after a
  // rebuild has thrown the card away, and there's no artboard to re-pad then.
  const artboard = overlay.box && overlay.box.parentNode;
  if (!artboard) return;

  layout(overlay);
  padForOverlays(artboard);
  window.pymChild.sendHeight();
}


function createOverlay(d, artboard) {
  // Point names start with a letter here, but the attribute form is used
  // anyway so filename-derived ids beginning with a digit also work. Every
  // artboard repeats the same ids, so the lookup stays scoped to the artboard.
  //
  // Scoped to the svg as well, so a text object that happens to share a point's
  // name can't be picked up instead: a div where a path is expected fails in
  // addPulse().
  const anchor = artboard.querySelector(`svg [id="${d.pointId}"]`);
  if (!anchor) return null;

  const svg = anchor.ownerSVGElement;
  if (!svg) {
    // The trigger layer exported as a linked file instead of inline SVG.
    // Rename the Illustrator layer to `trigger:svg,inline` and re-export.
    console.warn(`Point "${d.pointId}" is not inside an inline <svg>.`);
    return null;
  }

  const i = breakpointIndex(artboard);
  const size = valueAt(d.sizes, i);
  const orientation = valueAtStr(d.orientation, i);
  const lineStyle = valueAtStr(d.lineStyle, i);

  const overlay = {
    // Null on a row with no asset. See below.
    box: null,
    anchor,
    svg,
    src: d.src,
    orientation: orientation || autoOrientation(anchor, artboard, size),
    lineStyle,
    poster: d.poster,
    hOffset: valueAt(d.hOffsets, i),
    vOffset: valueAt(d.vOffsets, i),
    size,
    img: null,
    video: null,
    media: null,
    caption: null,
    button: null,
    line: null,
    // Every dot this card lights, its own first. Only the anchor positions the
    // card and takes the leader line; the rest are named in the sheet's
    // `point-name2` and light and pulse alongside it. A name with no dot on the
    // board is skipped rather than treated as an error, so an artboard that
    // hasn't caught up with the sheet still works.
    highlights: [anchor]
      .concat(d.alsoLight
        .map(id => artboard.querySelector(`svg [id="${id}"]`))
        .filter(Boolean))
      .map(node => ({ node, pulse: addPulse(node) })),
    // The map label for this point: a text object on the Illustrator
    // `trigger-text` layer named for its point plus `-text`, so `point1-text`
    // goes with `point1`. Optional — a point with no label still works, and a
    // board without the layer just has none of them.
    label: artboard.querySelector(`${LABEL_SELECTOR}[id="${d.pointId}-text"]`),
    loaded: false,
    top: 0,
  };

  // A row with no asset is a stop on the river rather than a card: its point
  // lights, its label comes up, it takes its turn in the sequence and hands over
  // like any other, but nothing opens beside it. The opening point is one of
  // these — the glacier the flood came off is named on the map itself and has no
  // clip to show — which is why the graphic can start on it.
  //
  // Everything downstream that touches a card checks `box` for this.
  if (!d.src) return overlay;

  const box = document.createElement('div');

  // Hidden from the first paint. The reveal used to be fail-open — a card was
  // visible until tick() had a position to hide it against — but the parent
  // takes a poll or two to answer, and every card on the map showed at once for
  // that window. revealAll() is what puts them back if no parent ever answers.
  box.className = 'overlay-container fade';
  box.id = `overlay-${artboard.id}-${d.pointId}`;
  overlay.box = box;

  // The sound button sits over the clip, so the two share a positioning
  // context.
  const media = document.createElement('div');
  media.className = 'overlay-media';
  box.appendChild(media);
  overlay.media = media;

  if (d.isVideo) {
    const video = document.createElement('video');

    // Critical iOS attributes. `src` is set later, on approach.
    video.muted = true;
    video.playsInline = true;
    video.loop = true;
    video.controls = false;
    video.preload = 'auto';
    video.disablePictureInPicture = true;

    video.setAttribute('playsinline', 'true');
    video.setAttribute('webkit-playsinline', 'true');
    video.setAttribute('muted', 'true');
    video.setAttribute('loop', 'true');

    video.className = 'overlay-video';

    // A video has no dimensions until its metadata arrives, so the first layout
    // pass can't know whether the clip is portrait or how tall the card will be.
    // Clips here are a mix of both orientations, so this matters.
    video.addEventListener('loadedmetadata', () => relayout(overlay));

    media.appendChild(video);

    if (d.hasAudio) {
      const button = document.createElement('button');
      button.type = 'button';
      button.className = 'overlay-sound';
      button.addEventListener('click', () => toggleSound(overlay));
      media.appendChild(button);
      overlay.button = button;
    }

    overlay.video = video;
    setSoundButton(overlay, false);
  } else {
    const img = document.createElement('img');
    img.alt = stripTags(d.caption);
    img.className = 'overlay-image';

    // An image arriving changes the popup's height, which moves the leader
    // line and the iframe's own height. The point it reveals against doesn't
    // move, so the cached offset stays good.
    img.addEventListener('load', () => relayout(overlay));

    media.appendChild(img);
    overlay.img = img;
  }

  // Wrapped rather than appended straight to the box, so the caption is one
  // child of the card's column and the gap under the clip stays a single gap
  // however many lines the copy runs to.
  const caption = document.createElement('div');
  caption.className = 'overlay-caption';

  if (d.caption || d.credit) {
    const text = document.createElement('div');
    text.className = 'overlay-caption-text';
    text.innerHTML = d.caption || '';

    // Set inline at the end of the copy rather than on a line of its own, so a
    // credit reads as part of the sentence it belongs to. The space is a node
    // rather than CSS because the two are one run of text, not two boxes.
    if (d.credit) {
      const credit = document.createElement('span');
      credit.className = 'overlay-credit';
      credit.innerHTML = `(${d.credit})`;

      if (d.caption) text.appendChild(document.createTextNode(' '));
      text.appendChild(credit);
    }

    caption.appendChild(text);
  }

  if (caption.childNodes.length) {
    box.appendChild(caption);
    overlay.caption = caption;
  }

  artboard.appendChild(box);
  layout(overlay);

  return overlay;
}


/* ------------------------------------------------------------------ *
 * Assets
 * ------------------------------------------------------------------ */

function load(overlay) {
  if (overlay.loaded) return;
  overlay.loaded = true;

  if (overlay.img) {
    overlay.img.src = overlay.src;
  } else if (overlay.video) {
    // Ahead of the src, so the still is on screen while the clip downloads
    // rather than after it. A poster is a fraction of a clip's weight, so on a
    // slow connection this is the difference between a card fading in with a
    // picture in it and one fading in empty.
    if (overlay.poster) overlay.video.poster = overlay.poster;

    overlay.video.src = overlay.src;
    overlay.video.load();
  }
}


function revealAll() {
  if (gotPosition) return;

  // Everything is built hidden, so this is the only thing that puts it on screen
  // when there's no parent to scroll against — a graphic opened on its own shows
  // every clip at once rather than an empty map.
  overlays.forEach((o) => {
    if (o.box) o.box.classList.add('inview');
    if (o.line) o.line.classList.add('inview');

    load(o);
    if (o.video) play(o.video);
  });

  triggers.forEach(t => t.node.classList.add('fade', 'inview'));
  labels.forEach(l => l.node.classList.add('is-active'));
}


/* ------------------------------------------------------------------ *
 * Flood line
 * ------------------------------------------------------------------ */

// Reveals the line up to `drawn`, measured from the top of the map, over `ms`.
// Setting the dash offset is the whole of it — the CSS transition does the
// drawing, so this costs nothing per frame.
function drawTrace(drawn, ms) {
  const { path, length, reversed } = trace;
  const to = Math.max(0, Math.min(length, drawn));
  const offset = reversed ? -(length - to) : length - to;

  path.style.transition = ms > 0
    ? `stroke-dashoffset ${Math.round(ms)}ms ease-in-out`
    : 'none';
  path.style.strokeDashoffset = offset;
  trace.drawn = to;
}


// Runs the line out to a card's point, or back to nothing when passed no card.
// Reversing is the same operation: the reader scrolling up shortens the line the
// way scrolling down grew it.
function traceTo(overlay) {
  if (!trace) return;

  const drawn = overlay ? overlay.drawn || 0 : 0;
  const travel = Math.abs(drawn - trace.drawn) * TRACE_MS_PER_UNIT;

  drawTrace(drawn, Math.min(TRACE_MAX_MS, Math.max(TRACE_MIN_MS, travel)));
}


// A fixed walk of the line, taken once. getPointAtLength() is the expensive part
// of everything below, and the alternative is paying for the whole walk again
// for every dot on the map.
function sampleTrace(path, length) {
  const samples = [];
  const step = length / TRACE_SAMPLES;

  for (let at = 0; at <= length; at += step) {
    const on = path.getPointAtLength(at);
    samples.push({ at, x: on.x, y: on.y });
  }

  return samples;
}


// Centre of a map dot in SVG user units. The line and the dots are separate
// ai2html SVGs, but they share the same viewBox, so this lands in the line's
// coordinate system without a screen-space conversion.
//
// Read from the path's moveto first. getBBox() can come back empty on iOS
// before the first layout pass, which would put every dot at the origin and
// flip the reveal direction.
function anchorCenter(anchor) {
  const d = anchor.getAttribute('d');
  const match = d && d.match(/^M\s*([-\d.eE+]+)\s*,\s*([-\d.eE+]+)/i);

  if (match) {
    return { x: parseFloat(match[1]), y: parseFloat(match[2]) };
  }

  const box = anchor.getBBox();
  return { x: box.x + (box.width / 2), y: box.y + (box.height / 2) };
}


// How far along the line a map coordinate sits. Walk the line, keep the
// closest place to the target.
function traceLengthAtXY(x, y) {
  const { path, length, samples } = trace;
  const away = (sx, sy) => ((sx - x) ** 2) + ((sy - y) ** 2);

  let best = 0;
  let nearest = Infinity;

  samples.forEach((s) => {
    const distance = away(s.x, s.y);

    if (distance < nearest) {
      nearest = distance;
      best = s.at;
    }
  });

  // The walk lands within one sample of the true nearest place, and a sample is
  // several map pixels of winding river — enough to put the join between two
  // legs on the wrong side of a dot. So that window gets looked at properly.
  const step = length / TRACE_SAMPLES;
  const from = Math.max(0, best - step);

  for (let i = 0; i <= TRACE_REFINE; i += 1) {
    const at = Math.min(length, from + ((i * step * 2) / TRACE_REFINE));
    const on = path.getPointAtLength(at);
    const distance = away(on.x, on.y);

    if (distance < nearest) {
      nearest = distance;
      best = at;
    }
  }

  return best;
}


function traceLengthAt(anchor) {
  const { x, y } = anchorCenter(anchor);
  return traceLengthAtXY(x, y);
}


// Which way the reveal runs: glacier (point1) toward the bridge (point8).
// Illustrator can export the path in opposite directions on different artboards.
function syncTraceReversed(artboard) {
  if (!trace) return;

  const { path, length } = trace;
  const byPathY = path.getPointAtLength(0).y > path.getPointAtLength(length).y;
  const top = artboard.querySelector('svg [id="point1"]');
  const bottom = artboard.querySelector('svg [id="point8"]');

  if (top && bottom) {
    const topAt = traceLengthAt(top);
    const bottomAt = traceLengthAt(bottom);

    // When the dots couldn't be placed — both landed on the same stretch, which
    // is what an empty getBBox on iOS produces — fall back to the path ends.
    if (Math.abs(topAt - bottomAt) > length * 0.01) {
      trace.reversed = topAt > bottomAt;
      return;
    }
  }

  trace.reversed = byPathY;
}


// Where the line leaves the frame, measured from the top of the map. Illustrator
// carried the path on past the last dot and off the bottom of the artboard, so
// without this the reader is left at the end of the graphic looking at a line
// that stops a couple of hundred pixels short of the edge for no reason.
function traceEdge() {
  const {
    path, length, reversed, samples,
  } = trace;
  const box = path.ownerSVGElement.viewBox.baseVal;

  return samples.reduce((edge, s) => {
    const inside = s.x >= box.x && s.x <= box.x + box.width
      && s.y >= box.y && s.y <= box.y + box.height;
    const drawn = reversed ? length - s.at : s.at;

    return inside && drawn > edge ? drawn : edge;
  }, 0);
}


// Finds the line on the artboard and hides it, ready to be drawn. Called on
// every build, since a breakpoint change swaps the artboard underneath us and
// the artboards don't all carry the layer.
function setupTrace(artboard) {
  trace = null;

  const group = artboard.querySelector(`[id$="-${TRACE_LAYER}-img"]`);
  const path = group && group.querySelector('path');
  if (!path || !path.getTotalLength) return;

  const length = path.getTotalLength();
  if (!length) return;

  // ai2html marks everything it exports non-scaling-stroke, which takes the dash
  // pattern out of the path's own coordinate system and hands it to the screen's.
  // Every length here comes from getTotalLength(), which is in the path's, and
  // the two have to agree or the reveal lands in the wrong place. Set inline
  // because the generated rule that turns it on is an id selector.
  path.removeAttribute('vector-effect');
  path.style.vectorEffect = 'none';
  path.style.strokeDasharray = `${length} ${length}`;

  trace = {
    path,
    length,
    reversed: false,
    drawn: 0,
    samples: sampleTrace(path, length),
  };

  syncTraceReversed(artboard);
  drawTrace(0, 0);
}


/* ------------------------------------------------------------------ *
 * Scroll tracking
 * ------------------------------------------------------------------ */

// Which point the timed opening is holding the screen on, or -1 once it has let
// go. While this answers with a point, the scroll mechanism is not consulted at
// all: the opening advances on its own clock, which the poll in listen() keeps
// turning whether or not the reader is moving.
//
// The opening lets go on the reader scrolling past its last point, rather than
// the moment the clock runs out on it. Waiting is what keeps the sequence from
// snapping backwards: the reader hasn't moved, so `want` is still up at the
// glacier where they started, and nextCard() answers with `want` whenever
// nothing below the current card is showable yet.
function introCard(want, now) {
  if (introOver || !order.length) return -1;

  // Above the first point — scrolled back off the top of the map, or the parent
  // hasn't settled yet. Hold the clock rather than start the opening against a
  // stretch of river the reader isn't looking at.
  if (want === -1) {
    introFrom = 0;
    return -1;
  }

  // Gone past the opening under their own steam, so it has nothing left to say.
  const last = Math.min(INTRO_HOLDS.length, order.length - 1);
  if (order.indexOf(want) > last) {
    introOver = true;
    return -1;
  }

  if (!introFrom) introFrom = now;

  let step = 0;
  let spent = 0;

  while (step < last && now - introFrom >= spent + INTRO_HOLDS[step]) {
    spent += INTRO_HOLDS[step];
    step += 1;
  }

  return order[step];
}


// Which card to hand the screen to, given `want` — the point furthest down the
// river the reader has gone by.
//
// Handing straight over to `want` skips points, and two things about this map
// make that bite. The graphic sits at the top of its own page, so on load the
// reader is often already past the first point or two and the opening image never
// gets a turn. And points 5, 6 and 7 are within 13px of each other, so the reader
// crosses all three between two reads and only the last is ever asked for.
//
// So a handover steps one point down the river instead of jumping. What keeps
// that from lagging behind the reader is the check that the point is still on
// screen: a card whose point has already gone up past SHOW_UNTIL is passed over,
// and where a whole run of them has gone by the step falls through to `want`.
// Points sitting on top of each other are all on screen together, which is what
// gives each of them a turn.
function nextCard(want, reached, onPoint, bars, back) {
  // Above the first point: nothing to show.
  if (want === -1) return -1;

  // Its turn has started and its point hasn't left the screen. The two readings
  // come from different places, and the line each one is measured against is
  // per point rather than REVEAL_AT flat — see tick().
  const showable = i => reached[i] > bars[i] && onPoint[i] <= SHOW_UNTIL;

  const earliest = () => {
    const found = order.find(showable);
    return found === undefined ? want : found;
  };

  // Opening card, and the way back if `shown` has gone from the order. The first
  // point still on screen, rather than the last one passed, is what puts the
  // reader at the top of the river on load.
  const at = shown === -1 ? -1 : order.indexOf(shown);
  if (at === -1) return earliest();

  // Scrolling back up, but not far enough to have left the card: stay on it.
  //
  // A portrait clip can stand taller than the phone it's being read on, so a
  // reader who arrives at one and wants to see the top of it has to scroll up to
  // get it — and handing the screen backwards for that swaps the clip out from
  // under the gesture meant to look at it. Reading a card is the one time going
  // back up isn't a request to go back.
  //
  // The card's own line is what says whether the reader has really left: it
  // holds until its point has drifted back down to where it came up, which is
  // most of a screen of room to move around in, and no room at all once the
  // reader has genuinely gone up past where the card began.
  //
  // Past that, one card at a time, which is the walk below run in reverse.
  //
  // The earliest card still on screen is the other way to answer this, and it is
  // what this used to do. But three or four points are on screen together for
  // most of the mobile map, so going back skipped nearly to the top of them in a
  // single handover while going forward stepped one point at a time — and none of
  // the spacing the triggers are laid out with applies to a jump, because that
  // spacing is all measured between neighbours.
  //
  // Kept as the fallback for a walk that finds nothing, which is the top of the
  // map: points 1, 9 and 2 are all above the reveal line as soon as the map is in
  // view, and there is little scroll left up there to take another step in.
  if (back) {
    if (showable(shown)) return shown;

    for (let rank = at - 1; rank >= 0; rank -= 1) {
      if (showable(order[rank])) return order[rank];
    }

    return earliest();
  }

  for (let rank = at + 1; rank < order.length; rank += 1) {
    if (showable(order[rank])) return order[rank];
  }

  // A whole run of points has gone up past the top of the screen. Catch up to
  // where the reader actually is rather than sitting on a card behind them.
  //
  // Forwards only. `want` is the last point gone by, so for as long as the
  // reader sits between two points it names the card already up — and the
  // moment a trigger is crossed, a fraction of a pixel either way flips it
  // between that card and the one before. Answering with it there hands the
  // screen backwards, and since the gates reopen every MIN_SHOW_MS the two
  // then trade it back and forth for as long as the reader stays put.
  return order.indexOf(want) > at ? want : shown;
}


// Parent payload: viewportWidth viewportHeight iframeTop iframeLeft
//                 iframeBottom iframeRight
function tick(parentInfo) {
  const positions = parentInfo.split(' ').map(pos => parseFloat(pos));
  const viewportHeight = positions[1];
  const iframeTop = positions[2];
  const iframeBottom = positions[4];

  // Reports about somebody else's iframe, thrown away before they can move the
  // sequence.
  //
  // A pym child accepts any message carrying its id, and its id is the id of
  // the container the embed sits in — so two parents built around one container
  // are indistinguishable to it, and both answer every poll. Production makes
  // that pair: the embed ships an inline `new pym.Parent(...)` snippet inside a
  // div marked `data-type="dailygraphics"`, which is also what CNN's own embed
  // scanner looks for. Whichever parent loses the container keeps a reference to
  // an iframe that is no longer in the document, and a detached element measures
  // as a rect of zeros, so its answers put the frame at the top of the page at
  // full height. Interleaved with the real ones ten times a second, they read as
  // the reader having jumped back above the map and then returned.
  //
  // The frame's own height is the tell. A live iframe is as tall as this
  // document, because the parent sized it from sendHeight(); a dead one is as
  // tall as nothing at all. Compared loosely, since a report can be in flight
  // while the document is growing under it.
  const framed = iframeBottom - iframeTop;

  if (!Number.isFinite(framed)
    || framed < document.documentElement.scrollHeight / 2) {
    if (!warnedStray) {
      warnedStray = true;
      console.warn(
        `Ignoring position reports for a ${framed}px frame; this one is `
        + `${document.documentElement.scrollHeight}px. Two pym parents are `
        + 'answering for this embed.',
        parentInfo,
      );
    }

    return;
  }

  // // ---- !!!!!!!! ---- ////
  // Bail out before anything is hidden if the parent sent us garbage.
  // The `fade` class is only ever applied after a good read, so a parent
  // that never answers leaves the whole graphic visible instead of blank.
  // // ---- !!!!!!!! ---- ////
  if (!Number.isFinite(viewportHeight)
    || !Number.isFinite(iframeTop)
    || viewportHeight <= 0) return;

  gotPosition = true;

  // How far a trigger may be spaced from its point depends on the screen, which
  // is only known from here. The first read always lands, since the fallback the
  // build pass used is never the reader's actual height to the pixel.
  if (Math.abs(viewportHeight - parentViewport) > VIEWPORT_SLOP) {
    parentViewport = viewportHeight;
    spaceTriggers();

    // Both halves of the runway just moved: the screen it's measured in, and the
    // last trigger it's measured from.
    const board = visibleArtboard();
    if (board) {
      padForOverlays(board);
      window.pymChild.sendHeight();
    }
  }

  // 1 at the top of the parent viewport, 0 at the bottom.
  const progress = top => 1 - ((top + iframeTop) / viewportHeight);

  triggers.forEach((t) => {
    t.node.classList.add('fade');
    t.node.classList.toggle('inview', progress(t.top) > REVEAL_AT);
  });

  // Two readings per overlay, because spaceTriggers() puts a card's turn a
  // little below its own point. The trigger says when the turn starts; the point
  // says whether the card is still worth putting up, since a card whose point
  // has left the screen renders off screen with it.
  const reached = overlays.map(o => progress(o.trigger));
  const onPoint = overlays.map(o => progress(o.top));

  // REVEAL_AT assumes there's always scroll left to carry a point that far up
  // the screen, and for the last point on the map there isn't: point 8 sits
  // 204px above the end of the artboard, so the page runs out of scroll with it
  // still about 70% of the way down the screen and its clip never came up at
  // all. Where a point can't reach the shared line, its own ceiling stands in,
  // less a margin so it still arrives with some scroll to spare rather than on
  // the very last pixel.
  //
  // Worst case on purpose: this assumes nothing sits below the iframe on the
  // parent page, and a page with more below it only ever gives a point further
  // to climb than this allows for.
  const lowestTop = viewportHeight - document.documentElement.scrollHeight;
  const ceiling = top => 1 - ((top + lowestTop) / viewportHeight);
  const bars = overlays.map(o => Math.min(REVEAL_AT, ceiling(o.trigger) - REVEAL_MARGIN));

  // A label no card speaks for reveals against its own position and then stays,
  // rather than taking a turn the way a card does. It has nothing to hand over
  // to — the closing line is the last thing on the map — and there's no clip
  // underneath it whose place on the river it could be confused for.
  labels.forEach((l) => {
    const bar = Math.min(REVEAL_AT, ceiling(l.top) - REVEAL_MARGIN);
    l.node.classList.toggle('is-active', progress(l.top) > bar);
  });

  // Only one clip is on screen at a time. Every point the reader has already
  // gone by is still past its line, so the current one is the last one passed:
  // of those revealed, the one with the least progress. Picking by geometry
  // rather than sheet row matters — asset numbering doesn't follow the point
  // order down the river.
  let want = -1;

  reached.forEach((p, i) => {
    if (p > bars[i] && (want === -1 || p < reached[want])) want = i;
  });

  // A handover needs both gates open: the card up has had its turn, so a point
  // close behind the one before it can't take the screen off a card mid-fade;
  // and the reader has moved since it came up, so the sequence doesn't walk on
  // by itself while the page sits still. The first card is exempt from both —
  // there's nothing on screen yet to give a turn to, or to scroll away from.
  //
  // Nothing has to schedule the end of a hold: the poll in listen() runs this
  // ten times a second whether or not the reader is moving.
  const now = Date.now();
  const hold = opened ? MIN_SHOW_MS : OPENING_SHOW_MS;

  // Positive means the iframe's top edge has moved down the screen, so the
  // reader has scrolled up.
  const moved = iframeTop - shownTop;

  const show = (next) => {
    if (next === shown) return;

    // Set on the way out of the opening card, not into it.
    if (shown !== -1) opened = true;

    shown = next;
    shownAt = now;
    shownTop = iframeTop;

    traceTo(overlays[shown]);
  };

  // The opening runs on a clock and answers -1 once it's done, which is when the
  // gates below start governing.
  const intro = introCard(want, now);

  // The scroll gate is a bet that more scrolling is coming, and at the end of the
  // graphic that bet is lost: the reader has arrived and stopped, and no amount
  // of waiting will produce the pixels the gate is holding out for.
  //
  // Which is what strands the bottom of the map after a fast flick. The handover
  // walks one point at a time, so a reader who crosses the whole river in one
  // gesture lands with the sequence several points behind. It takes one more step
  // on the scroll banked during the flick, and that step spends it — the next
  // handover measures from where the card came up, which is where the page now
  // sits — so the last point or two never come up at all.
  //
  // Only the scroll half is waived. The hold still applies, so what the reader
  // gets on landing is the remaining cards settling out one at a time rather than
  // the end of the map snapping into place. There are never many: nextCard()
  // passes over points that have already climbed off the top of the screen, which
  // after a flick to the bottom is most of them.
  const grounded = iframeTop <= lowestTop + SCROLL_STEP;

  const ready = shown === -1
    || (now - shownAt >= hold && (grounded || Math.abs(moved) >= SCROLL_STEP));

  if (intro !== -1) {
    show(intro);
  } else if (ready) {
    // Scrolling back up has to be a real gesture, not a stray pixel. Any
    // upward reading at all used to count, which is safe only while the scroll
    // gate below is also holding out for SCROLL_STEP — once `grounded` waives
    // it, a single jittered pixel would send the sequence back to earliest().
    show(nextCard(want, reached, onPoint, bars, moved >= SCROLL_STEP));
  }

  const active = shown;

  overlays.forEach((o, i) => {
    // Start fetching before the reader gets here so nothing pops in late.
    if (reached[i] > -PRELOAD_VIEWPORTS) load(o);

    const inview = i === active;

    if (o.box) {
      o.box.classList.add('fade');
      o.box.classList.toggle('inview', inview);
    }

    if (o.line) {
      o.line.classList.add('fade');
      o.line.classList.toggle('inview', inview);
    }

    // Mark the point on the map that this clip belongs to.
    setHighlight(o, inview);
    if (o.label) o.label.classList.toggle('is-active', inview);

    if (o.video) {
      if (inview && o.video.paused) {
        play(o.video);
      } else if (!inview && !o.video.paused) {
        o.video.pause();
        // Back to the first frame, so a clip the reader returns to plays from
        // the start rather than resuming mid-loop.
        o.video.currentTime = 0;
        // Drop back to silent, so scrolling back to a clip never starts
        // playing sound the reader didn't just ask for.
        mute(o);
      }
    }
  });
}


function listen() {
  if (listening) return;
  listening = true;

  window.pymChild.onMessage('viewport-iframe-position', tick);

  setInterval(() => {
    window.pymChild.getParentPositionInfo();
  }, POLL_MS);
}


/* ------------------------------------------------------------------ *
 * Build
 * ------------------------------------------------------------------ */

// Furthest a trigger may sit past its own point, in pixels. Grows with the
// reader's screen, because what makes a delay too long is the point scrolling
// away before the trigger it's waiting on comes up.
function maxTriggerDelay() {
  return (SHOW_UNTIL - REVEAL_AT) * (parentViewport || FALLBACK_VIEWPORT)
    * TRIGGER_DELAY_MARGIN;
}


// How much scroll to give each card before the next one may take over, walking
// the map top down. There is only so much river to go round — the points span a
// fixed stretch of artboard — so this is a question of dividing it up, not of
// picking a number.
//
// Stacked pairs get the long turns, since they're the ones that can't work
// otherwise, and the rest of the map pays for them out of an even split of
// what's left. Nobody's share is allowed to run past the next point on the map:
// a card that reaches for more than the river gives it only drags the following
// trigger away from its own point, and every trigger downstream after that.
//
// The floor is allowed to overrun the river. The mobile map is 1536px of it and
// the floors ask 1820px, which pushes every trigger below the shortfall further
// from its own point than the map alone could carry it — the last one lands
// 206px past its point, well beyond the end of the artboard. What makes that
// survivable is padForOverlays() holding page below the last trigger wherever it
// ends up, so the overrun comes out as scroll after the map rather than as
// triggers that never fire. Capping the floor to what the river can afford is
// the other way to balance this, and it costs a fifth of every card's turn
// across the whole map to save a couple of hundred pixels of height.
function triggerGaps() {
  const tops = order.map(i => overlays[i].top);
  const natural = tops.slice(0, -1).map((top, n) => tops[n + 1] - top);
  const stacked = natural.map(gap => gap < CLUSTER_WITHIN);

  const span = tops[tops.length - 1] - tops[0];
  const spare = stacked.filter(s => !s).length;
  const share = spare
    ? (span - (stacked.filter(Boolean).length * CLUSTER_TRIGGER_GAP)) / spare
    : 0;

  return natural.map((gap, n) => (stacked[n]
    ? CLUSTER_TRIGGER_GAP
    : Math.max(MIN_TRIGGER_GAP, Math.min(share, gap))));
}


// Spaces out the scroll positions at which cards take over. A card's turn runs
// until the next point's trigger, so points close together on the map leave the
// first of them with next to no turn: points 6 and 7 are 4px apart, which is not
// enough scroll to watch anything.
//
// A trigger is only ever pushed later than its own point, never earlier, so a
// card still can't appear before the reader has reached it. Where a gap can't be
// honoured without dragging a trigger further from its point than the viewport
// allows, that pair settles for a shorter turn rather than have nextCard() throw
// the card out for being off screen.
function spaceTriggers() {
  const gaps = triggerGaps();
  const delay = maxTriggerDelay();
  let previous = -Infinity;

  order.forEach((i, n) => {
    const o = overlays[i];
    const spaced = Math.max(o.top, previous + (n ? gaps[n - 1] : 0));

    o.trigger = Math.min(spaced, o.top + delay);
    previous = o.trigger;
  });
}


// Labels on the `trigger-text` layer that belong to no point on the map. A label
// is normally named for its point — `point2-text` goes with `point2` — and comes
// and goes with that point's card. One named for a point the artboard hasn't got
// can never be switched on that way, and that's how a step with a place on the
// river but nothing to show there is written: a label and no dot.
//
// Keyed off whether the dot exists rather than off which labels the cards took,
// so a label whose point is on the map keeps its card's timing in the two cases
// where no card claimed it — a row not filled in yet, and a companion point
// named in `point-name2`, which lights with the card that speaks for it.
//
// `anchor` is what the entry reveals against, which for a label is itself. The
// arrows are the one thing here that reveals against something other than its
// own position, so that they and the note they belong to arrive together.
function spareLabels(artboard) {
  const entries = Array.from(artboard.querySelectorAll(LABEL_SELECTOR))
    .filter(node => !artboard.querySelector(`svg [id="${node.id.replace(/-text$/, '')}"]`))
    .map(node => ({ node, anchor: node, top: 0 }));

  const arrows = artboard.querySelector(ARROW_SELECTOR);
  const anchor = artboard.querySelector(`${LABEL_SELECTOR}[id="${ARROW_LABEL}"]`);

  // Either can be missing on an artboard that hasn't caught up with the others,
  // and the rest of the map still works without them.
  if (arrows && anchor) entries.push({ node: arrows, anchor, top: 0 });

  return entries;
}


// A clip reveals when its point on the river reaches REVEAL_AT, not when its
// popup does — the popup can sit well above or below the point once offsets
// are dialled in, and the point is what the reader is actually looking at.
function measure() {
  triggers.forEach((t) => { t.top = documentTop(t.node); });
  labels.forEach((l) => { l.top = documentTop(l.anchor); });
  overlays.forEach((o) => { o.top = documentTop(o.anchor); });

  order = overlays
    .map((o, i) => i)
    .sort((a, b) => overlays[a].top - overlays[b].top);

  spaceTriggers();

  const artboard = visibleArtboard();
  if (!artboard) return;

  // How much of the flood line each card runs it out to, measured from the top
  // of the map so the numbers grow the way the reader scrolls.
  if (trace) {
    syncTraceReversed(artboard);

    overlays.forEach((o) => {
      const at = traceLengthAt(o.anchor);
      o.drawn = trace.reversed ? trace.length - at : at;
    });

    // Nothing follows the last card, so it takes the line to the edge of the
    // frame instead of stopping it dead at the dot.
    const last = overlays[order[order.length - 1]];
    if (last) last.drawn = Math.max(last.drawn, traceEdge());

    const target = shown >= 0 && overlays[shown] ? (overlays[shown].drawn || 0) : 0;
    drawTrace(target, 0);
  }
}


function build() {
  const artboard = visibleArtboard();
  if (!artboard) return;

  document.querySelectorAll('.overlay-container').forEach(el => el.remove());
  document.querySelectorAll('.overlay-line').forEach(el => el.remove());
  document.querySelectorAll('.point-pulse').forEach(el => el.remove());
  overlays.forEach(o => setHighlight(o, false));

  // Labels and arrows belong to the artboard, so unlike the popups they survive a
  // rebuild and would otherwise stay lit for a clip that no longer exists.
  document.querySelectorAll(`${LABEL_SELECTOR}.is-active, ${ARROW_SELECTOR}.is-active`)
    .forEach(el => el.classList.remove('is-active'));

  overlays = [];
  data.forEach((d) => {
    const overlay = createOverlay(row(d), artboard);
    if (overlay) overlays.push(overlay);
  });

  // Indexes into the list just rebuilt, so an old hold would point at a card
  // that no longer exists.
  shown = -1;
  shownAt = 0;
  shownTop = 0;
  opened = false;

  // A rebuild is a breakpoint change, and the opening is cheap to replay. Where
  // the reader is already down the map it won't be: introCard() lets go on the
  // first read that puts them past the border crossing.
  introFrom = 0;
  introOver = false;

  triggers = Array.from(artboard.querySelectorAll(ANNOTATION_SELECTOR))
    .map(node => ({ node, top: 0 }));
  labels = spareLabels(artboard);

  setupTrace(artboard);
  measure();
  padForOverlays(artboard);
  window.pymChild.sendHeight();
  listen();

  // Nothing is hidden until the parent answers, so a graphic loaded outside a
  // pym parent stays readable — but its assets would never be fetched.
  clearTimeout(fallbackTimer);
  fallbackTimer = setTimeout(revealAll, FALLBACK_MS);
}


function scheduleBuild() {
  clearTimeout(buildTimer);
  buildTimer = setTimeout(build, 250);
}


/* ------------------------------------------------------------------ *
 * Boot
 * ------------------------------------------------------------------ */

function draw() {
  if (process.env.NODE_ENV === 'development') {
    // the code below applies the ai2html
    // during development mode.

    const figure = document.querySelector('#ai2html-graphic');
    figure.innerHTML = ai2html;

    // If you want to write code for this graphic,
    // move this stuff outside the if block that it's in.
  }

  scheduleBuild();
}

window.pymChild = new Child({ renderCallback: draw, id: pkg.name });

console.clear();


// The iframe is resized by the parent every time we report a new height, which
// fires `resize` in here. Rebuilding on that would loop, so only a real width
// change (a new artboard) is worth reacting to.
window.addEventListener('resize', () => {
  if (window.innerWidth === lastWidth) return;
  lastWidth = window.innerWidth;
  scheduleBuild();
});


window.onload = () => {
  // necessary evil for right now

  if (process.env.NODE_ENV === 'development') {
    Object.keys(meta).forEach((id) => {
      const node = document.querySelector(`.${id}`);

      if (node) {
        node.innerHTML = meta[id];
      }
    });
  }

  const { pymChild } = window;
  pymChild.sendHeight();

  // Ask the parent to allow inline video playback. Parents that don't
  // implement this handler fall back to a paused poster frame.
  pymChild.sendMessage('iframe-attributes', JSON.stringify({
    allow: 'autoplay; encrypted-media',
    playsinline: 'true',
  }));

  // If embedded, send Zion page view event
  if (window.pymChild.parentUrl) {
    const zion = new Zion(pkg.name, pymChild.parentUrl.split('?')[0]);
  }

  scheduleBuild();
};
